import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Cookies } from "react-cookie";
import { IChannel, ITextFieldCollection, RootStateType } from "@Interfaces";
import { openCompletionRedirectDialog } from "@Components/Dialog/CompletionRedirectDialog/openCompletionRedirectDialog";
import { getPollIdsFromCookies, readGuestVotes } from "@Utils/guestVotes";
import { isUserChannelAdmin } from "@Utils/isUserChannelAdmin";
import { readSubmittedTextFieldIds } from "@Utils/submittedTextFields";
import {
    CompletionRedirectStatus,
    getCompletionRedirectStatus,
    getCompletionRedirectUrl,
    getNextUnansweredPollExpiry,
} from "./completionRedirect.utils";

// setTimeout fires immediately past ~24.8 days; voting time limits can be longer (720h).
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

// Same sources PollItem uses to show a returning guest their earlier votes.
const readLocallyVotedPollIds = (): string[] => {
    const fromStorage = readGuestVotes().map((vote) => vote.pollId);
    const fromCookie = getPollIdsFromCookies({
        tb: new Cookies().get("tb"),
    });
    return Array.from(new Set([...fromStorage, ...fromCookie]));
};

type UseCompletionRedirectParams = {
    channel: IChannel | null | undefined;
    textFields: ITextFieldCollection[] | null;
    submittedTextFieldIds: string[];
};

/**
 * Sends participants to the channel's completion redirect URL:
 * - straight away when there is nothing to answer (incl. closed events) or when they
 *   arrive already finished;
 * - via the thank-you dialog when they finish during this visit.
 * Double-vote channels never auto-redirect a returning participant (they may be back
 * for the second round); they get the continue link instead.
 */
export const useCompletionRedirect = ({
    channel,
    textFields,
    submittedTextFieldIds,
}: UseCompletionRedirectParams) => {
    const dispatch = useDispatch<any>();
    const userId = useSelector((state: RootStateType) => state.auth.user?.id);
    const channelId = channel?._id;
    const redirectUrl = getCompletionRedirectUrl(channel);
    const allowDoubleVote = Boolean(channel?.settings?.allow_double_vote);
    const isChannelAdmin = isUserChannelAdmin(channel, userId);
    // Whether this visit has seen the channel unfinished; decides dialog vs. instant redirect.
    const sawIncompleteRef = useRef(false);
    const hasRedirectedRef = useRef(false);
    // Returning to a double-vote channel already finished: show the continue link, never redirect.
    const [isReturningDoubleVoter, setIsReturningDoubleVoter] = useState(false);
    // Clock for poll expiry; advanced when an unanswered poll expires so completion is re-judged.
    const [now, setNow] = useState(() => Date.now());

    // Snapshot on arrival only: local storage is written as soon as a choice is clicked,
    // before the 5s undo window ends, while in-session votes are confirmed by the server (vote_id).
    const previouslyVotedPollIds = useMemo(
        () => (channelId ? readLocallyVotedPollIds() : []),
        [channelId]
    );
    // Snapshot too, so a text field answered during this visit counts as in-session.
    const previouslySubmittedTextFieldIds = useMemo(
        () => (channelId ? readSubmittedTextFieldIds() : []),
        [channelId]
    );

    const status: CompletionRedirectStatus = useMemo(
        () =>
            redirectUrl
                ? getCompletionRedirectStatus({
                      channel,
                      textFields,
                      submittedTextFieldIds,
                      previouslyVotedPollIds,
                      previouslySubmittedTextFieldIds,
                      isChannelAdmin,
                      now,
                  })
                : "disabled",
        [
            channel,
            textFields,
            submittedTextFieldIds,
            previouslyVotedPollIds,
            previouslySubmittedTextFieldIds,
            isChannelAdmin,
            redirectUrl,
            now,
        ]
    );

    // A poll left unanswered stops blocking completion once it expires; nothing else would
    // re-render the page then, so wake up at the next expiry.
    useEffect(() => {
        if (status !== "incomplete") {
            return undefined;
        }

        const nextExpiry = getNextUnansweredPollExpiry({
            channel,
            previouslyVotedPollIds,
            now,
        });
        if (nextExpiry === null) {
            return undefined;
        }

        const delay = Math.min(
            Math.max(nextExpiry - Date.now(), 0),
            MAX_TIMEOUT_MS
        );
        const timer = setTimeout(() => setNow(Date.now()), delay);
        return () => clearTimeout(timer);
    }, [channel, now, previouslyVotedPollIds, status]);

    // Each channel (e.g. a sub-channel picked from the lobby) starts a fresh evaluation.
    useEffect(() => {
        sawIncompleteRef.current = false;
        hasRedirectedRef.current = false;
        setIsReturningDoubleVoter(false);
    }, [channelId]);

    useEffect(() => {
        if (
            hasRedirectedRef.current ||
            isReturningDoubleVoter ||
            !redirectUrl
        ) {
            return;
        }

        if (status === "incomplete" || status === "pollsDone") {
            sawIncompleteRef.current = true;
            return;
        }

        const arrivedFinished =
            status === "complete" && !sawIncompleteRef.current;

        if (arrivedFinished && allowDoubleVote) {
            setIsReturningDoubleVoter(true);
            return;
        }

        if (status === "empty" || arrivedFinished) {
            hasRedirectedRef.current = true;
            // replace, so Back doesn't land on a page that redirects again.
            window.location.replace(redirectUrl);
            return;
        }

        if (status === "complete") {
            hasRedirectedRef.current = true;
            dispatch(openCompletionRedirectDialog(redirectUrl));
        }
    }, [
        allowDoubleVote,
        dispatch,
        isReturningDoubleVoter,
        redirectUrl,
        status,
    ]);

    const showContinueLink =
        Boolean(redirectUrl) &&
        (status === "pollsDone" ||
            (status === "complete" && isReturningDoubleVoter));

    return {
        redirectUrl,
        showContinueLink,
    };
};
